import { ArrowRight, ShieldCheck, Zap, Clock3 } from "lucide-react";
import { DTORBIT_URL } from "@/constants";
import RateCard from "./RateCard";
import { Reveal } from "./Reveal";

const HeroVisual = () => (
    <div
        className="relative w-full max-w-[420px] mx-auto aspect-square select-none animate-float-soft"
        role="img"
        aria-label="USDT to INR conversion process illustration"
        data-testid="hero-visual"
    >
        <svg viewBox="0 0 480 480" className="w-full h-full" fill="none">
            <defs>
                <linearGradient id="coinUsdt" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1E56FF" />
                    <stop offset="100%" stopColor="#3EC6F0" />
                </linearGradient>
                <linearGradient id="coinInr" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#0A1F9E" />
                    <stop offset="100%" stopColor="#1E56FF" />
                </linearGradient>
                <linearGradient id="arrowGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3EC6F0" />
                    <stop offset="100%" stopColor="#1E56FF" />
                </linearGradient>
                <radialGradient id="glowCenter" cx="0.5" cy="0.5" r="0.5">
                    <stop offset="0%" stopColor="#1E56FF" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#1E56FF" stopOpacity="0" />
                </radialGradient>
            </defs>

            <circle cx="240" cy="240" r="235" fill="url(#glowCenter)" />

            <g className="animate-spin-slower">
                <circle
                    cx="240"
                    cy="240"
                    r="196"
                    stroke="#1E56FF"
                    strokeOpacity="0.28"
                    strokeWidth="1.2"
                    strokeDasharray="3 9"
                />
                <circle cx="240" cy="44" r="7" fill="#1E56FF" />
                <circle cx="240" cy="44" r="12" fill="#1E56FF" fillOpacity="0.15" />
            </g>
            <circle
                cx="240"
                cy="240"
                r="146"
                stroke="#3EC6F0"
                strokeOpacity="0.45"
                strokeWidth="1"
            />

            <path
                className="animate-dash"
                d="M192 218 Q 240 262 292 248"
                stroke="url(#arrowGrad)"
                strokeWidth="5"
                strokeLinecap="round"
            />
            <path
                d="M292 248 l-14 -7 m14 7 l-13 8"
                stroke="#1E56FF"
                strokeWidth="5"
                strokeLinecap="round"
            />

            <g>
                <circle cx="152" cy="196" r="62" fill="url(#coinUsdt)" />
                <circle
                    cx="152"
                    cy="196"
                    r="62"
                    stroke="#FFFFFF"
                    strokeOpacity="0.5"
                    strokeWidth="2"
                />
                <circle
                    cx="152"
                    cy="196"
                    r="48"
                    stroke="#FFFFFF"
                    strokeOpacity="0.35"
                    strokeWidth="1.4"
                />
                <text
                    x="152"
                    y="214"
                    textAnchor="middle"
                    fontFamily="Outfit, sans-serif"
                    fontWeight="800"
                    fontSize="52"
                    fill="#FFFFFF"
                >
                    T
                </text>
                <text
                    x="152"
                    y="288"
                    textAnchor="middle"
                    fontFamily="Outfit, sans-serif"
                    fontWeight="700"
                    fontSize="19"
                    letterSpacing="3"
                    fill="#3D5A99"
                >
                    USDT
                </text>
            </g>

            <g>
                <circle cx="336" cy="288" r="62" fill="url(#coinInr)" />
                <circle
                    cx="336"
                    cy="288"
                    r="62"
                    stroke="#3EC6F0"
                    strokeOpacity="0.5"
                    strokeWidth="2"
                />
                <circle
                    cx="336"
                    cy="288"
                    r="48"
                    stroke="#FFFFFF"
                    strokeOpacity="0.3"
                    strokeWidth="1.4"
                />
                <text
                    x="336"
                    y="308"
                    textAnchor="middle"
                    fontFamily="Outfit, sans-serif"
                    fontWeight="800"
                    fontSize="50"
                    fill="#FFFFFF"
                >
                    ₹
                </text>
                <text
                    x="336"
                    y="380"
                    textAnchor="middle"
                    fontFamily="Outfit, sans-serif"
                    fontWeight="700"
                    fontSize="19"
                    letterSpacing="3"
                    fill="#3D5A99"
                >
                    INR
                </text>
            </g>
        </svg>

        <div className="absolute top-2 right-2 glass rounded-xl px-3.5 py-2.5 flex items-center gap-2 animate-float">
            <ShieldCheck className="w-4 h-4 text-[#1E56FF]" />
            <span className="text-xs font-semibold text-[#0F1B3D]">Secure</span>
        </div>
        <div className="absolute bottom-6 left-0 glass rounded-xl px-3.5 py-2.5 flex items-center gap-2 animate-float-delayed">
            <Zap className="w-4 h-4 text-[#1E56FF]" />
            <span className="text-xs font-semibold text-[#0F1B3D]">Fast Processing</span>
        </div>
    </div>
);

const TRUST_POINTS = [
    { icon: ShieldCheck, label: "Secure Transactions" },
    { icon: Zap, label: "Fast Processing" },
    { icon: Clock3, label: "24/7 Support" },
];

export default function Hero({ rate }) {
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative overflow-hidden pt-28 md:pt-40 pb-14 md:pb-24"
        >
            <div className="pointer-events-none absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#DCEAFF] blur-[120px]" />
            <div className="pointer-events-none absolute top-20 -right-40 w-[420px] h-[420px] rounded-full bg-[#D9F3FD] blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-grid-light" aria-hidden="true" />
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <span className="particle w-2 h-2" style={{ top: "16%", left: "7%" }} />
                <span className="particle w-1.5 h-1.5" style={{ top: "30%", left: "46%", animationDelay: "1.4s" }} />
                <span className="particle w-2.5 h-2.5" style={{ top: "12%", right: "12%", animationDelay: "2.2s" }} />
                <span className="particle w-1.5 h-1.5" style={{ top: "58%", left: "3%", animationDelay: "0.8s" }} />
                <span className="particle w-2 h-2" style={{ top: "66%", right: "6%", animationDelay: "3s" }} />
                <span className="particle w-1.5 h-1.5" style={{ top: "40%", right: "30%", animationDelay: "1.9s" }} />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
                <div>
                    <Reveal>
                        <span
                            data-testid="hero-badge"
                            className="inline-flex items-center gap-2.5 glass rounded-full px-4 py-2 text-xs font-semibold tracking-wide text-[#1E56FF]"
                        >
                            <span className="live-dot" />
                            Live USDT → INR Exchange Rate
                        </span>
                    </Reveal>

                    <Reveal delay={80}>
                        <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] font-extrabold tracking-tight text-[#0F1B3D]">
                            Best <span className="text-gradient">USDT to INR</span> Exchange
                        </h1>
                    </Reveal>

                    <Reveal delay={160}>
                        <p className="mt-4 text-base md:text-lg font-semibold text-[#24355E]">
                            Fast, Secure &amp; Reliable USDT to INR Exchange
                        </p>
                        <p className="mt-3 text-sm md:text-base text-[#5B6B8C] leading-relaxed max-w-xl">
                            Exchange USDT to INR with DtOrbit using competitive
                            live rates, secure processing and a simple exchange
                            experience.
                        </p>
                    </Reveal>

                    <Reveal delay={240}>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href={DTORBIT_URL}
                                data-testid="hero-start-exchange-button"
                                className="btn-primary"
                                aria-label="Start USDT to INR Exchange on DtOrbit"
                            >
                                Start USDT to INR Exchange
                                <ArrowRight className="w-4 h-4" />
                            </a>
                            <a
                                href="#how-it-works"
                                data-testid="hero-how-it-works-link"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E2A4A] hover:text-[#1E56FF] transition-colors px-2 py-3"
                            >
                                See How It Works
                                <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={320}>
                        <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
                            {TRUST_POINTS.map(({ icon: Icon, label }) => (
                                <span
                                    key={label}
                                    className="inline-flex items-center gap-2 text-xs font-medium text-[#5B6B8C]"
                                >
                                    <Icon className="w-4 h-4 text-[#1E56FF]" />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={400}>
                        <div className="mt-8 max-w-md">
                            <RateCard rate={rate} />
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={200} className="lg:pl-6">
                    <HeroVisual />
                </Reveal>
            </div>
        </section>
    );
}
